import { useState, useEffect } from 'react'
import { Head, router } from '@inertiajs/react'
import MainLayout from '@/components/Layout/MainLayout'
import { ContactsToolbar } from '@/components/contacts/ContactsToolbar'
import { ContactsTable } from '@/components/contacts/ContactsTable'
import { ContactFormModal } from '@/components/contacts/ContactFormModal'
import { VisitorDetailDrawer } from '@/components/inbox/VisitorDetailDrawer'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'
import type { CurrentUser } from '@/types/shared'
import type Visitor from '@/types/serializers/Visitor'
import type { Pagy, Filters } from '@/components/contacts/types'

interface ContactsProps {
  current_user: CurrentUser
  contacts: Visitor[]
  pagy: Pagy
  filters: Filters
  contact_detail?: Visitor | null
  can_delete_contact?: boolean
}

export default function Contacts({
  current_user,
  contacts,
  pagy,
  filters,
  contact_detail,
  can_delete_contact = false,
}: ContactsProps) {
  const { withAccount } = useRouteParams()
  const [showNewModal, setShowNewModal] = useState(false)
  const [editContact, setEditContact] = useState<Visitor | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)

  // Open drawer when contact_detail arrives from server
  useEffect(() => {
    setDrawerOpen(!!contact_detail)
  }, [contact_detail])

  const handleDrawerClose = () => {
    // Close immediately (local state), then navigate to clear URL
    setDrawerOpen(false)
    router.visit(routes.contacts.index.path(withAccount()), {
      preserveScroll: true,
    })
  }

  const handleEdit = () => {
    if (contact_detail) setEditContact(contact_detail)
  }

  const handleDelete = () => {
    if (!contact_detail) return
    setDrawerOpen(false)
    router.delete(routes.contacts.destroy.path(withAccount({ id: contact_detail.id })))
  }

  return (
    <>
      <Head title="Contacts" />
      <MainLayout user={current_user} breadcrumbs={[{ label: 'Contacts' }]}>
        <div className="flex flex-1 flex-col min-h-0">
          <ContactsToolbar
            count={pagy.count}
            filters={filters}
            onNewContact={() => setShowNewModal(true)}
          />
          <ContactsTable
            contacts={contacts}
            pagy={pagy}
            filters={filters}
          />
        </div>

        {contact_detail && (
          <VisitorDetailDrawer
            visitor={contact_detail}
            open={drawerOpen}
            onOpenChange={(open) => { if (!open) handleDrawerClose() }}
            onEdit={handleEdit}
            onDelete={can_delete_contact ? handleDelete : undefined}
          />
        )}

        <ContactFormModal
          mode="create"
          open={showNewModal}
          onOpenChange={setShowNewModal}
        />

        {editContact && (
          <ContactFormModal
            mode="edit"
            contact={editContact}
            open
            onOpenChange={(open) => { if (!open) setEditContact(null) }}
          />
        )}
      </MainLayout>
    </>
  )
}
